import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Factory } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

const num = (v) => (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

export function ProduceConfirmDialog({ mo, onOpenChange, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [coPackers, setCoPackers] = useState([]);
  const [coPackerId, setCoPackerId] = useState("");
  const isAlum = (mo?.product_name || "").toLowerCase().includes("alum");

  useEffect(() => {
    if (!mo) return;
    setPreview(null);
    setBusy(false);
    setCoPackerId("");
    api.get(`/manufacturing-orders/${mo.id}/produce-preview`)
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't load the component requirements for this MO"));
    if ((mo.product_name || "").toLowerCase().includes("alum")) {
      api.get("/co-packers").then((r) => setCoPackers(r.data || [])).catch(() => {});
    }
  }, [mo]);

  if (!mo) return null;

  const cases = preview && preview.case_pack ? preview.ordered_bottles / preview.case_pack : null;

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/manufacturing-orders/${mo.id}/produce`, isAlum ? { co_packer_id: coPackerId } : {});
      toast.success(`${preview?.mwo_number || "MO"} produced — components consumed, report pending`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Failed to produce"));
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!mo} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm" data-testid="produce-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Factory className="h-5 w-5 text-[#002FA7]" /> Confirm Production</DialogTitle>
          <DialogDescription>
            Consumes the exact theoretical BOM quantities now and releases this MO's reservations.
            Waste and variance are reconciled later by the Production Report.
          </DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-[140px_1fr] gap-y-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Manufacturing Order</span>
              <span className="font-semibold text-slate-900" data-testid="produce-mo-number">{preview.mwo_number}</span>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Product</span>
              <span className="text-slate-800">{preview.product_name}{preview.design_name ? ` — ${preview.design_name}` : ""}</span>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Production Quantity</span>
              <span className="tabular-nums text-slate-800" data-testid="produce-qty">
                {num(preview.ordered_bottles)} bottles{cases ? ` / ${num(cases)} cases` : ""}
              </span>
            </div>
            {isAlum && (
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Produced At (Co-Packer)</p>
                <Select value={coPackerId || undefined} onValueChange={setCoPackerId}>
                  <SelectTrigger className="h-9 w-full rounded-sm" data-testid="produce-copacker-select">
                    <SelectValue placeholder="Choose the co-packer that produced this run" />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm">
                    {coPackers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-400">Components are deducted from this co-packer's balance; finished cases land in your warehouse stock.</p>
              </div>
            )}
            <div className="border border-slate-200">
              <p className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-600">Components to Consume</p>
              <div className="max-h-56 overflow-y-auto">
                {preview.items.map((it, i) => (
                  <div key={`${it.name || ""}-${it.type || ""}-${i}`} className="flex items-center justify-between gap-3 border-b border-slate-100 px-3 py-1.5 text-xs last:border-b-0" data-testid={`produce-item-${i}`}>
                    <span className="min-w-0 truncate text-slate-700">{it.name}{it.type ? ` · ${it.type}` : ""}</span>
                    <span className="whitespace-nowrap font-semibold tabular-nums text-slate-900">{num(it.qty)} {it.unit}</span>
                  </div>
                ))}
                {!preview.items.length && <p className="px-3 py-2 text-xs text-slate-400">No consumable components on this MO's BOM.</p>}
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={busy} data-testid="produce-cancel">Cancel</Button>
          <Button className="rounded-sm bg-[#002FA7] hover:bg-[#00227a]" onClick={confirm} disabled={busy || !preview || !preview.items?.length || (isAlum && !coPackerId)} data-testid="produce-confirm">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Confirm Production
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
